import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Check, Zap, TrendingUp, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/coming-soon-modal.css'; // Reusing backdrop styles

const UpgradeModal = ({ isOpen, onClose, feature, context }) => {
    const navigate = useNavigate();
    const [selectedSpecialPlan, setSelectedSpecialPlan] = useState('gold');

    const handleUpgrade = () => {
        onClose();
        if (isFree && usedCount >= 3) {
            navigate(`/pricing?plan=${selectedSpecialPlan}`);
        } else if (isLPU && usedCount >= 3) {
            navigate(`/pricing?plan=gold_lpu`);
        } else if (feature === 'studio') {
            navigate('/studio');
        } else {
            navigate('/pricing');
        }
    };
    
    const getNextMonday = () => {
        const now = new Date();
        const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
        const nextMonday = new Date(now);
        nextMonday.setDate(now.getDate() + daysUntilMonday);
        nextMonday.setHours(0, 0, 0, 0);
        return nextMonday.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    };

    const formatTierLabel = (tierValue) => {
        if (!tierValue) return 'Free';
        if (typeof tierValue !== 'string') return 'Free';
        const normalized = tierValue.trim();
        if (!normalized) return 'Free';
        return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
    };

    const getContent = () => {
        switch (feature) {
            case 'visuals':
                return {
                    title: "Ready for Stunning Visuals?",
                    description: "You've sparked all the visual creativity included in your plan! ✨ Upgrade now to instantly unlock higher visual quotas and keep producing vibrant, AI-crafted imagery for your lessons. Let's make your lessons beautiful!"
                };
            case 'scenarios':
                return {
                    title: "Level Up Your Scenarios!",
                    description: "Scenario learning is a game-changer, and you've used your current limit. 🚀 Elevate your teaching by upgrading today for a massive boost in scenario-building capacity! Your students will love the challenge."
                };
            case 'exercises':
                return {
                    title: "Time for More Practice!",
                    description: "Your students are excelling with these exercises! 📚 Upgrade to get higher exercise volume and unlock even richer in-class practice sets instantly. Keep that learning momentum high!"
                };
            case 'multilingual':
                return {
                    title: "Ready for Multilingual Lessons?",
                    description: "You've generated all the multilingual lessons allowed in your plan! 🌍 Upgrade today to translate and create lessons in multiple languages simultaneously without restrictions."
                };
            case 'inclass_quiz':
                return {
                    title: "Unlock More In-Class Quizzes!",
                    description: "Engage your classroom with interactive quizzes! You've used your current limit. 🚀 Upgrade now to get a higher in-class quiz quota and keep the interactive learning going!"
                };
            case 'lessons':
                const isWeekly = (context?.[0] || "").toLowerCase().includes('weekly') || (context?.reason || "").toLowerCase().includes('weekly');
                return {
                    title: "You're a Lesson Machine!",
                    description: isWeekly 
                        ? `You've done incredible work and reached your weekly peak! 🎯 Don't let the flow stop—upgrade today and enjoy higher limits to keep your teaching workflow unstoppable! Why wait until ${getNextMonday()}?`
                        : "You've successfully used all your lesson generations for the month! 🌟 Upgrade now to unlock higher generation limits and continue creating world-class materials without skipping a beat."
                };
            case 'studio':
                return {
                    title: "Unlock Creative Freedom!",
                    description: "Ready to build exactly what you want? 🎨 Upgrade to Studio to upload your own notes, fine-tune every content block, and fully control your lesson curriculum. Your content, your way!"
                };
            default:
                return {
                    title: "Supercharge Your Teaching!",
                    description: "Unlock your full potential! ⚡ Upgrade to access premium presentation downloads, engaging scenario-based quizzes, and lightning-fast AI generation with no boundaries. Join our elite educators today!"
                };
        }
    };

    const { user } = useAuth() || {};
    
    // Usage data - prioritize live user object from context, fallback to snapshot context
    const currentUsage = user ? {
        monthly: {
            used: user.lessons_this_month ?? 0,
            quota: user.lessons_quota ?? 4,
            isUnlimited: (user.lessons_quota || 0) >= 999999
        },
        weekly: {
            used: user.weekly_used ?? 0,
            cap: user.weekly_cap ?? 1
        },
        visuals: {
            used: user.visuals_generated_count || 0,
            cap: user.max_visuals || null
        },
        scenarios: {
            used: user.scenarios_generated_count || 0,
            cap: user.max_scenarios || null
        },
        exercises: {
            used: user.exercises_generated_count || 0,
            cap: user.max_exercises || null,
            perGenerationLimit: user.exercise_per_generation || 1
        },
        multilingual: {
            used: user.multilingual_generated_count || 0,
            cap: user.max_multilingual_generations || null
        },
        inclassQuiz: {
            used: user.inclass_quiz_generated_count || 0,
            cap: user.max_inclass_quiz_generations || null
        }
    } : (context?.usage || {
        monthly: { used: 0, quota: 4, isUnlimited: false },
        weekly: { used: 0, cap: 1 },
        visuals: { used: 0, cap: null },
        scenarios: { used: 0, cap: null },
        exercises: { used: 0, cap: null, perGenerationLimit: 1 },
        multilingual: { used: 0, cap: null },
        inclassQuiz: { used: 0, cap: null }
    });

    const usage = currentUsage;
    const currentTier = formatTierLabel(user?.subscription_tier || context?.currentTier || 'Free');
    const exhausted = Array.isArray(context?.exhausted) ? context.exhausted : [];

    const isFree = String(user?.subscription_tier || '').toLowerCase() === 'free';
    const isLPU = String(user?.subscription_tier || '').toLowerCase() === 'lpu_faculty';
    const usedCount = user?.lessons_this_month ?? 0;
    const isSpecialOffer = (isFree || isLPU) && usedCount >= 3;

    // Build feature-specific quota highlight for the triggered feature
    const featureQuotaHighlight = (() => {
        switch (feature) {
            case 'visuals': return {
                label: 'Visual Generations',
                used: Number(usage.visuals?.used ?? 0),
                cap: Number.isFinite(usage.visuals?.cap) ? usage.visuals.cap : null,
                perLesson: null,
                icon: '🎨'
            };
            case 'scenarios': return {
                label: 'Scenario Generations',
                used: Number(usage.scenarios?.used ?? 0),
                cap: Number.isFinite(usage.scenarios?.cap) ? usage.scenarios.cap : null,
                perLesson: null,
                icon: '🎭'
            };
            case 'exercises': return {
                label: 'Exercise Generations',
                used: Number(usage.exercises?.used ?? 0),
                cap: Number.isFinite(usage.exercises?.cap) ? usage.exercises.cap : null,
                perLesson: Number.isFinite(usage.exercises?.perGenerationLimit) ? usage.exercises.perGenerationLimit : null,
                icon: '📝'
            };
            case 'multilingual': return {
                label: 'Multilingual Generations',
                used: Number(usage.multilingual?.used ?? 0),
                cap: Number.isFinite(usage.multilingual?.cap) ? usage.multilingual.cap : null,
                perLesson: null,
                icon: '🌍'
            };
            case 'inclass_quiz': return {
                label: 'In-Class Quiz Generations',
                used: Number(usage.inclassQuiz?.used ?? 0),
                cap: Number.isFinite(usage.inclassQuiz?.cap) ? usage.inclassQuiz.cap : null,
                perLesson: null,
                icon: '✏️'
            };
            case 'lessons': 
                const isWkly = (context?.reason || "").toLowerCase().includes('weekly');
                return {
                    label: isWkly ? 'Weekly Lessons' : 'Monthly Lessons',
                    used: isWkly ? Number(usage.weekly?.used ?? 0) : Number(usage.monthly?.used ?? 0),
                    cap: isWkly ? Number(usage.weekly?.cap ?? 0) : (usage.monthly?.isUnlimited ? null : Number(usage.monthly?.quota ?? 0)),
                    perLesson: null,
                    icon: '📚'
                };
            default: return null;
        }
    })();

    const usageRows = [
        {
            key: 'weeklyGenerations',
            label: 'Weekly lesson generations',
            value: usage.weekly?.cap !== null
                ? `${Number(usage.weekly?.used || 0)} / ${Number(usage.weekly?.cap || 0)}`
                : 'Not capped on this tier'
        },
        {
            key: 'monthlyGenerations',
            label: 'Monthly lesson generations',
            value: usage.monthly?.isUnlimited
                ? 'Unlimited'
                : `${Number(usage.monthly?.used || 0)} / ${Number(usage.monthly?.quota || 0)}`
        },
        {
            key: 'scenarioGenerations',
            label: 'Scenario generations',
            value: Number.isFinite(usage.scenarios?.cap)
                ? `${Number(usage.scenarios?.used || 0)} / ${Number(usage.scenarios?.cap || 0)}`
                : 'Not capped on this tier'
        },
        {
            key: 'visualGenerations',
            label: 'Visual generations',
            value: Number.isFinite(usage.visuals?.cap)
                ? `${Number(usage.visuals?.used || 0)} / ${Number(usage.visuals?.cap || 0)}`
                : 'Not capped on this tier'
        },
        {
            key: 'exerciseGenerations',
            label: 'Exercise generations',
            value: Number.isFinite(usage.exercises?.cap)
                ? `${Number(usage.exercises?.used || 0)} / ${Number(usage.exercises?.cap || 0)}`
                : 'Not capped on this tier'
        },
        {
            key: 'multilingualGenerations',
            label: 'Multilingual generations',
            value: Number.isFinite(usage.multilingual?.cap)
                ? `${Number(usage.multilingual?.used || 0)} / ${Number(usage.multilingual?.cap || 0)}`
                : 'Not capped on this tier'
        },
        {
            key: 'inclassQuizGenerations',
            label: 'In-class quiz generations',
            value: Number.isFinite(usage.inclassQuiz?.cap)
                ? `${Number(usage.inclassQuiz?.used || 0)} / ${Number(usage.inclassQuiz?.cap || 0)}`
                : 'Not capped on this tier'
        }
    ];

    const planLimitations = [
        `Tier: ${currentTier}`,
        Number.isFinite(usage.monthly?.quota) && !usage.monthly?.isUnlimited
            ? `Monthly lessons limit: ${Number(usage.monthly?.quota || 0)}`
            : 'Monthly lessons: Unlimited',
        Number.isFinite(usage.exercises?.perGenerationLimit)
            ? `Exercises per lesson: up to ${Number(usage.exercises?.perGenerationLimit || 0)}`
            : 'Exercises per lesson: As per plan',
        Number.isFinite(usage.multilingual?.cap)
            ? `Multilingual limit: ${Number(usage.multilingual?.cap || 0)} generations`
            : 'Multilingual: Unlimited',
        Number.isFinite(usage.inclassQuiz?.cap)
            ? `In-class quiz limit: ${Number(usage.inclassQuiz?.cap || 0)} generations`
            : 'In-class quiz: Unlimited',
        'Advanced limits increase with Silver, Gold, and Platinum plans'
    ];

    const content = getContent();

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="modal-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.5)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            zIndex: 9998
                        }}
                    />

                    {/* Modal */}
                    <motion.div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            zIndex: 9999,
                            padding: '40px 20px' // Increased top/bottom padding
                        }}
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
                    >
                        <div
                            className="upgrade-modal"
                            style={{
                                pointerEvents: 'auto',
                                background: '#ffffff',
                                borderRadius: '28px',
                                padding: '40px 32px',
                                maxWidth: '480px',
                                width: '100%',
                                maxHeight: 'calc(100vh - 80px)', // Ensure it doesn't hit screen edges
                                overflowY: 'auto', // Scrollable if content is long
                                boxShadow: '0 40px 100px -10px rgba(30, 27, 75, 0.2), 0 0 80px rgba(245, 158, 11, 0.1)',
                                position: 'relative',
                                overflowX: 'hidden',
                                border: '1px solid rgba(245, 158, 11, 0.2)'
                            }}
                        >
                            {/* Decorative Background Elements */}
                            <div style={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, background: 'radial-gradient(circle, rgba(255,215,0,0.15) 0%, transparent 70%)', borderRadius: '50%' }} />

                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '20px',
                                    background: '#1E1B4B',
                                    border: 'none',
                                    color: '#ffffff',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    zIndex: 10
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#111'; e.currentTarget.style.background = '#e5e7eb'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.background = '#f3f4f6'; }}
                            >
                                <X size={20} />
                            </button>

                            {/* Content */}
                            <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                                {isSpecialOffer ? (
                                    <>
                                        {/* SPECIAL PROMOTIONAL MODAL CONTENT */}
                                        <motion.div
                                            initial={{ scale: 0, rotate: -20 }}
                                            animate={{ scale: 1, rotate: 0 }}
                                            transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                background: 'linear-gradient(135deg, #1E1B4B, #2E2A75)',
                                                borderRadius: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 28px',
                                                boxShadow: '0 12px 24px rgba(30, 27, 75, 0.2)',
                                                transform: 'rotate(-5deg)'
                                            }}
                                        >
                                            <Crown size={40} color="#F59E0B" fill="#F59E0B" />
                                        </motion.div>

                                        <motion.h2
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                            style={{
                                                color: '#111827',
                                                fontSize: '28px',
                                                fontWeight: 800,
                                                marginBottom: '12px',
                                                letterSpacing: '-0.5px'
                                            }}
                                        >
                                            {isFree ? "Thank you for using TeachGenie! 💖" : "Exclusive LPU Faculty Offer! 🎓"}
                                        </motion.h2>

                                        <motion.p
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 }}
                                            style={{
                                                color: '#4b5563',
                                                fontSize: '15px',
                                                lineHeight: '1.6',
                                                marginBottom: '24px',
                                                fontWeight: 500
                                            }}
                                        >
                                            {isFree ? (
                                                <>
                                                    You are our <strong>first 1,000 users</strong>! As an early-adopting educator, please upgrade to Silver, Gold, or Platinum to keep generating world-class lessons. Special limited-time offer just for you, redeem it now.
                                                </>
                                            ) : (
                                                <>
                                                    Okay, so you are <strong>LPU Faculty</strong>! Thank you for using our platform to empower LPU classrooms. You've completed your <strong>3 trial generations</strong> — upgrade today to the premium Gold plan at a special LPU-exclusive price.
                                                </>
                                            )}
                                        </motion.p>

                                        {/* Interactivity Cards */}
                                        {isFree && (
                                            <div style={{ marginTop: '24px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
                                                <p style={{ fontSize: '12px', fontWeight: '800', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px', textAlign: 'center' }}>
                                                    🎁 Early Adopter Promotion
                                                </p>
                                                {[
                                                    { key: 'silver', name: 'Silver Plan', price: '₹99', original: '₹299', desc: '20 lessons/mo, Scenario Quiz (Presenting & Competitive Mode), 2 Multilingual', color: '#64748b' },
                                                    { key: 'gold', name: 'Gold Plan', price: '₹249', original: '₹599', desc: '40 lessons/mo, Scenario Quiz (Presenting & Competitive Mode), Visuals', color: '#f59e0b', popular: true },
                                                    { key: 'platinum', name: 'Platinum Plan', price: '₹499', original: '₹1,999', desc: '80 lessons/mo, Full Access', color: '#a855f7' }
                                                ].map((plan) => {
                                                    const isSelected = selectedSpecialPlan === plan.key;
                                                    return (
                                                        <motion.div
                                                            key={plan.key}
                                                            onClick={() => setSelectedSpecialPlan(plan.key)}
                                                            whileHover={{ scale: 1.02, y: -2 }}
                                                            whileTap={{ scale: 0.98 }}
                                                            style={{
                                                                padding: '16px 20px',
                                                                borderRadius: '16px',
                                                                border: isSelected ? `2.5px solid ${plan.color}` : '1.5px solid #e2e8f0',
                                                                background: isSelected ? 'rgba(99, 102, 241, 0.04)' : '#ffffff',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                position: 'relative',
                                                                boxShadow: isSelected ? '0 8px 20px rgba(99, 102, 241, 0.08)' : 'none'
                                                            }}
                                                        >
                                                            {plan.popular && (
                                                                <span style={{ position: 'absolute', top: '-10px', right: '20px', background: '#f59e0b', color: '#fff', fontSize: '9px', fontWeight: '900', padding: '3px 8px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Best Value
                                                                </span>
                                                            )}
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: plan.color }} />
                                                                    <span style={{ fontWeight: '800', fontSize: '15px', color: '#1e293b' }}>{plan.name}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                                                    <span style={{ fontSize: '18px', fontWeight: '900', color: '#1e293b' }}>{plan.price}</span>
                                                                    <s style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>{plan.original}</s>
                                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>/mo</span>
                                                                </div>
                                                            </div>
                                                            <p style={{ margin: 0, fontSize: '12.5px', color: '#64748b', fontWeight: '500', lineHeight: '1.4' }}>{plan.desc}</p>
                                                        </motion.div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {isLPU && (
                                            <div style={{ marginTop: '24px', textAlign: 'left', marginBottom: '32px' }}>
                                                <p style={{ fontSize: '12px', fontWeight: '800', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', textAlign: 'center' }}>
                                                    🎓 LPU Faculty Privilege
                                                </p>
                                                <motion.div
                                                    whileHover={{ y: -4 }}
                                                    style={{
                                                        padding: '24px',
                                                        borderRadius: '20px',
                                                        border: '2px solid #b91c1c',
                                                        background: 'linear-gradient(135deg, #fffcfc, #fff5f5)',
                                                        position: 'relative',
                                                        boxShadow: '0 15px 30px rgba(185, 28, 28, 0.08)'
                                                    }}
                                                >
                                                    <span style={{ position: 'absolute', top: '-11px', right: '24px', background: '#b91c1c', color: '#fff', fontSize: '9px', fontWeight: '900', padding: '4px 10px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        LPU Exclusive
                                                    </span>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                                                        <div>
                                                            <h4 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: '900', color: '#b91c1c' }}>GOLD PLAN</h4>
                                                            <span style={{ fontSize: '12.5px', color: '#7f1d1d', fontWeight: '700' }}>Unlimited Generations + Full Analytics</span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                                            <span style={{ fontSize: '28px', fontWeight: '900', color: '#b91c1c' }}>₹500</span>
                                                            <s style={{ fontSize: '14px', color: '#fca5a5', fontWeight: '600' }}>₹599</s>
                                                            <span style={{ fontSize: '11px', color: '#7f1d1d', fontWeight: '700' }}>/mo</span>
                                                        </div>
                                                    </div>
                                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#451a03', fontWeight: '600', lineHeight: '1.8' }}>
                                                        <li>Unlimited AI Lesson Generations</li>
                                                        <li>Full Scenario-Based Quiz Engine</li>
                                                        <li>Accreditation-aligned LO-PO Mapping</li>
                                                        <li>Exclusive Indian Knowledge System (IKS)</li>
                                                        <li>Classroom-ready Presentation PPT exports</li>
                                                    </ul>
                                                </motion.div>
                                            </div>
                                        )}

                                        {/* Action Buttons for Special Offer */}
                                        <div style={{ display: 'flex', gap: '12px' }}>
                                            <motion.button
                                                onClick={onClose}
                                                whileHover={{ backgroundColor: '#e5e7eb' }}
                                                whileTap={{ scale: 0.98 }}
                                                style={{
                                                    flex: 1,
                                                    height: '48px',
                                                    borderRadius: '50px',
                                                    background: '#f3f4f6',
                                                    border: 'none',
                                                    color: '#4b5563',
                                                    fontSize: '14.5px',
                                                    cursor: 'pointer',
                                                    fontWeight: '700',
                                                    transition: 'background 0.2s'
                                                }}
                                            >
                                                Maybe Later
                                            </motion.button>

                                            <motion.button
                                                onClick={handleUpgrade}
                                                whileHover={{ y: -2, boxShadow: '0 12px 24px rgba(30, 27, 75, 0.3)' }}
                                                whileTap={{ scale: 0.98 }}
                                                style={{
                                                    flex: 1.5,
                                                    height: '52px',
                                                    borderRadius: '50px',
                                                    background: isLPU ? 'linear-gradient(135deg, #b91c1c, #991b1b)' : 'linear-gradient(135deg, #1E1B4B, #2E2A75)',
                                                    border: 'none',
                                                    color: isLPU ? '#fff' : '#F59E0B',
                                                    fontSize: '14.5px',
                                                    cursor: 'pointer',
                                                    fontWeight: '800',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.2s',
                                                    boxShadow: '0 8px 16px rgba(30, 27, 75, 0.2)'
                                                }}
                                            >
                                                <Zap size={18} fill={isLPU ? '#fff' : '#F59E0B'} />
                                                Redeem Offer Now
                                            </motion.button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* ORIGINAL MODAL CONTENT */}
                                        <motion.div
                                            initial={{ scale: 0, rotate: -20 }}
                                            animate={{ scale: 1, rotate: 0 }}
                                            transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                background: 'linear-gradient(135deg, #1E1B4B, #2E2A75)',
                                                borderRadius: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 28px',
                                                boxShadow: '0 12px 24px rgba(30, 27, 75, 0.2)',
                                                transform: 'rotate(-5deg)'
                                            }}
                                        >
                                            <Crown size={40} color="#F59E0B" fill="#F59E0B" />
                                        </motion.div>

                                        <motion.h2
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                            style={{
                                                color: '#111827',
                                                fontSize: '28px',
                                                fontWeight: 800,
                                                marginBottom: '12px',
                                                letterSpacing: '-0.5px'
                                            }}
                                        >
                                            {content.title}
                                        </motion.h2>

                                        <motion.p
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 }}
                                            style={{
                                                color: '#4b5563',
                                                fontSize: '15px',
                                                lineHeight: '1.6',
                                                marginBottom: '24px',
                                                fontWeight: 500
                                            }}
                                        >
                                            {content.description}
                                        </motion.p>

                                        {context?.reason && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: 0.35 }}
                                                style={{
                                                    background: '#FEF3C7',
                                                    borderLeft: '4px solid #F59E0B',
                                                    padding: '12px 16px',
                                                    borderRadius: '8px',
                                                    marginBottom: '32px',
                                                    fontSize: '14px',
                                                    color: '#92400E',
                                                    textAlign: 'left',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px'
                                                }}
                                            >
                                                <AlertCircle size={20} color="#F59E0B" />
                                                <span style={{ fontWeight: 600 }}>{context.reason}</span>
                                            </motion.div>
                                        )}

                                        {/* Feature-specific quota highlight */}
                                        {featureQuotaHighlight && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.28 }}
                                                style={{
                                                    background: 'linear-gradient(135deg, #FFF7ED, #FEF3C7)',
                                                    border: '2px solid #F59E0B',
                                                    borderRadius: '16px',
                                                    padding: '20px 24px',
                                                    marginBottom: '20px',
                                                    textAlign: 'left',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <div style={{ position: 'absolute', top: -20, right: -20, fontSize: '60px', opacity: 0.12 }}>{featureQuotaHighlight.icon}</div>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                        {featureQuotaHighlight.icon} {featureQuotaHighlight.label}
                                                    </span>
                                                    <span style={{ background: '#DC2626', color: '#fff', fontSize: '9px', fontWeight: 900, padding: '3px 10px', borderRadius: '20px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Limit Reached</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
                                                    <span style={{ fontSize: '36px', fontWeight: 900, color: '#DC2626', lineHeight: 1 }}>
                                                        {featureQuotaHighlight.cap !== null ? featureQuotaHighlight.used : featureQuotaHighlight.used}
                                                    </span>
                                                    {featureQuotaHighlight.cap !== null && (
                                                        <span style={{ fontSize: '16px', fontWeight: 700, color: '#92400E', marginBottom: '4px' }}>
                                                            / {featureQuotaHighlight.cap} used
                                                        </span>
                                                    )}
                                                    {featureQuotaHighlight.cap === null && (
                                                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#92400E', marginBottom: '4px' }}>used this month</span>
                                                    )}
                                                </div>
                                                {featureQuotaHighlight.perLesson !== null && (
                                                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#92400E', fontWeight: 600 }}>
                                                        Your plan allows up to <strong>{featureQuotaHighlight.perLesson}</strong> exercise{featureQuotaHighlight.perLesson !== 1 ? 's' : ''} per lesson generation.
                                                    </p>
                                                )}
                                            </motion.div>
                                        )}

                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.35 }}
                                            style={{
                                                textAlign: 'left',
                                                background: 'rgba(30, 27, 75, 0.03)',
                                                border: '1px solid rgba(30, 27, 75, 0.1)',
                                                borderRadius: '16px',
                                                padding: '16px 20px',
                                                marginBottom: '20px'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#1E1B4B', fontWeight: 700 }}>
                                                <AlertCircle size={16} />
                                                <span>Plan Status</span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: 1.55 }}>
                                                You are currently on <strong>{currentTier}</strong>. Generation of {feature} is currently restricted based on your plan's limits.
                                            </p>
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.4 }}
                                            style={{
                                                textAlign: 'left',
                                                background: '#f8fafc',
                                                padding: '18px 20px',
                                                borderRadius: '14px',
                                                marginBottom: '18px',
                                                border: '1px solid #e2e8f0'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f172a', fontWeight: 700 }}>
                                                <TrendingUp size={16} />
                                                <span>Usage Snapshot</span>
                                            </div>
                                            {usageRows.map((row) => (
                                                <div
                                                    key={row.key}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        gap: '12px',
                                                        marginBottom: '8px',
                                                        padding: '6px 8px',
                                                        borderRadius: '10px',
                                                        background: exhausted.includes(row.key) ? '#fee2e2' : 'transparent',
                                                        color: exhausted.includes(row.key) ? '#991b1b' : '#334155',
                                                        fontWeight: exhausted.includes(row.key) ? 700 : 600,
                                                        fontSize: '13.5px'
                                                    }}
                                                >
                                                    <span>{row.label}</span>
                                                    <span>{row.value}</span>
                                                </div>
                                            ))}
                                        </motion.div>

                                        {/* Features List */}
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.4 }}
                                            style={{
                                                textAlign: 'left',
                                                background: '#f9fafb',
                                                padding: '24px',
                                                borderRadius: '20px',
                                                marginBottom: '32px',
                                                border: '1px solid #f3f4f6',
                                            }}
                                        >
                                            {planLimitations.map((limitText, idx) => (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: idx !== planLimitations.length - 1 ? '16px' : '0', color: '#111827', fontWeight: '600', fontSize: '15px' }}>
                                                    <div style={{ background: 'rgba(245, 158, 11, 0.1)', padding: '6px', borderRadius: '50%', marginRight: '14px', display: 'flex', color: '#F59E0B' }}>
                                                        <Check size={16} strokeWidth={3} />
                                                    </div>
                                                    <span>{limitText}</span>
                                                </div>
                                            ))}
                                        </motion.div>

                                        {/* Buttons */}
                                        <div style={{ display: 'flex', gap: '12px' }}>
                                            <motion.button
                                                onClick={onClose}
                                                whileHover={{ backgroundColor: '#e5e7eb' }}
                                                whileTap={{ scale: 0.98 }}
                                                style={{
                                                    flex: 1,
                                                    height: '48px',
                                                    borderRadius: '50px',
                                                    background: '#f3f4f6',
                                                    border: 'none',
                                                    color: '#4b5563',
                                                    fontSize: '15px',
                                                    cursor: 'pointer',
                                                    fontWeight: '700',
                                                    transition: 'background 0.2s'
                                                }}
                                            >
                                                Maybe Later
                                            </motion.button>

                                            <motion.button
                                                onClick={handleUpgrade}
                                                whileHover={{ y: -2, boxShadow: '0 12px 24px rgba(30, 27, 75, 0.3)' }}
                                                whileTap={{ scale: 0.98 }}
                                                style={{
                                                    flex: 1.5,
                                                    height: '52px',
                                                    borderRadius: '50px',
                                                    background: 'linear-gradient(135deg, #1E1B4B, #2E2A75)',
                                                    border: 'none',
                                                    color: '#F59E0B',
                                                    fontSize: '15px',
                                                    cursor: 'pointer',
                                                    fontWeight: '800',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.2s',
                                                    boxShadow: '0 8px 16px rgba(30, 27, 75, 0.2)'
                                                }}
                                            >
                                                <Zap size={18} fill="#F59E0B" />
                                                {feature === 'studio' ? 'Go to Studio' : 'View Upgrade Plans'}
                                            </motion.button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default UpgradeModal;
